import { useEffect } from 'react';
import { useRef, useState } from 'react';

import type { DropdownOption, DropdownOptionGroup } from '../../../Dropdown';
import type { OptionTypeWithExtras } from '../../MultiSelectDropdown';
import type { MultiSelectContainerProps } from '../MultiSelectContainer';

interface UseMultiSelectContainerResult<T = string> {
  groupedOptions?: (DropdownOption<OptionTypeWithExtras<T>> | DropdownOptionGroup<OptionTypeWithExtras<T>>)[];
}

export const CURRENTLY_SELECT_LABEL = 'currently selected';

export const SELECT_ALL_OPTIONS_VALUE = '*';
export const CLEAR_SELECTED_ITEMS_VALUE = 'CLEAR';

export const CLEAR_OPTION: DropdownOption<typeof CLEAR_SELECTED_ITEMS_VALUE> = {
  label: 'Clear selected items',
  value: CLEAR_SELECTED_ITEMS_VALUE,
};

export const makeSelectAllOption = (multiOptionLabel: string): DropdownOption<typeof SELECT_ALL_OPTIONS_VALUE> => ({
  label: multiOptionLabel,
  value: SELECT_ALL_OPTIONS_VALUE,
});

type OptionOrGroup<T> = DropdownOption<OptionTypeWithExtras<T>> | DropdownOptionGroup<OptionTypeWithExtras<T>>;

type OptionIdentity = { value: unknown; id?: string };

// Two options are the same selection only when both `value` and `id` match, mirroring
// `isOptionSelected` in `useMultiSelectDropdown`. Falls back to a value-only match when
// both ids are absent.
const optionMatches = (a: OptionIdentity, b: OptionIdentity): boolean => a.value === b.value && a.id === b.id;

const isSelectedIn = (selections: readonly OptionIdentity[], option: OptionIdentity): boolean =>
  selections.some((selection) => optionMatches(selection, option));

/**
 * Split a (possibly grouped) option list into the currently-selected leaf options and
 * the remaining unselected options/groups, preserving order.
 */
export const partitionBySelection = <T>(
  options: readonly OptionOrGroup<T>[],
  isSelected: (option: DropdownOption<OptionTypeWithExtras<T>>) => boolean
): { selected: DropdownOption<OptionTypeWithExtras<T>>[]; unselected: OptionOrGroup<T>[] } => {
  const selected: DropdownOption<OptionTypeWithExtras<T>>[] = [];
  const unselected: OptionOrGroup<T>[] = [];

  options.forEach((optionOrGroup) => {
    if ('value' in optionOrGroup) {
      (isSelected(optionOrGroup) ? selected : unselected).push(optionOrGroup);
      return;
    }

    const groupUnselected: DropdownOption<OptionTypeWithExtras<T>>[] = [];
    optionOrGroup.options.forEach((option) => {
      (isSelected(option) ? selected : groupUnselected).push(option);
    });
    if (groupUnselected.length) {
      unselected.push({ label: optionOrGroup.label, options: groupUnselected });
    }
  });

  return { selected, unselected };
};

// Options that are not currently selected, preserving order. Flat options are compared
// against `selectedFlat`; members inside groups are compared against `selectedGroupMembers`.
const rejectSelected = <T>(
  options: readonly OptionOrGroup<T>[],
  selectedFlat: readonly OptionIdentity[],
  selectedGroupMembers: readonly OptionIdentity[]
): OptionOrGroup<T>[] =>
  options
    .filter((optionOrGroup) => !('value' in optionOrGroup) || !isSelectedIn(selectedFlat, optionOrGroup))
    .map((optionOrGroup) =>
      'value' in optionOrGroup
        ? optionOrGroup
        : {
            label: optionOrGroup.label,
            options: optionOrGroup.options.filter((option) => !isSelectedIn(selectedGroupMembers, option)),
          }
    );

/**
 * Groups the dropdown options with any currently-selected items in a "currently selected"
 * group at the top, plus a "clear" option. The grouping is computed when the dropdown
 * opens; the "clear" option only appears when there is a value at open time, and the
 * "select all" option only exists when `multiOptionLabel` is provided.
 */
export default function useMultiSelectContainer<T = string>(
  props: MultiSelectContainerProps<T>
): UseMultiSelectContainerResult<T> {
  const { multiOptionLabel, multipleSelectControls } = props;
  const [groupedOptions, setGroupedOptions] = useState<
    Array<DropdownOption<OptionTypeWithExtras<T>> | DropdownOptionGroup<OptionTypeWithExtras<T>>>
  >([...props.options]);

  const initiallySelected = useRef([
    ...(props.value.length && !multipleSelectControls ? [CLEAR_OPTION] : []),
    ...props.value,
  ]);

  // The selection to float to the top while searching, re-captured from the live selection
  // whenever a new options set arrives (e.g. a new search response). Toggling a selection does
  // not change `props.options`, so the float order stays stable between responses.
  const floatSelection = useRef(props.value);
  const prevOptions = useRef(props.options);

  useEffect(() => {
    if (prevOptions.current !== props.options) {
      prevOptions.current = props.options;
      floatSelection.current = props.value;
    }

    const isFloated = (option: DropdownOption<OptionTypeWithExtras<T>>): boolean =>
      isSelectedIn(floatSelection.current, option);

    let options: OptionOrGroup<T>[];
    if (!props.inputValue) {
      const unselected = rejectSelected(props.options, initiallySelected.current, props.value);
      options = [{ label: CURRENTLY_SELECT_LABEL, options: initiallySelected.current }, ...unselected];
    } else {
      // While searching, float the currently-selected results to the top of the filtered list.
      // When nothing in the float set matches, keep the raw order so there is no empty
      // "currently selected" header.
      const { selected, unselected } = partitionBySelection(props.options, isFloated);
      options = selected.length ? [{ label: CURRENTLY_SELECT_LABEL, options: selected }, ...unselected] : unselected;
    }

    if (multiOptionLabel && !multipleSelectControls) {
      options = [
        {
          label: 'select all',
          options: [makeSelectAllOption(multiOptionLabel)],
        },
        ...options,
      ];
    }

    setGroupedOptions([...options]);
  }, [multiOptionLabel, props.options, props.value, props.inputValue, multipleSelectControls]);

  return { groupedOptions };
}
